import React, { useMemo } from 'react';
import { Modal } from '@crisellevaldez/speculo-ui';
import { formatDate } from '../../../../utils/dateTime';
import { sanitizeEmailHtml } from '../../../../utils/email/sanitizeEmailHtml';
import { useOrganizationStore } from '../../../../stores/useOrganizationStore';
import type { GQLMessageItem } from '../../../../hooks/graphql/contacts/useContact';

interface EmailPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  email: GQLMessageItem | null;
  /** Contact display name for the To row (Remi-sent) / From row (inbound). */
  contactName?: string;
  /** Contact email — when known (e.g. RecentActivities exposes
   *  `contactEmail`), shown alongside the contact name. Falls back to
   *  the message's fromAddress/toAddress otherwise. */
  contactEmail?: string;
}

/**
 * Email viewer modal — renders a stored message in a layout that reads
 * like a real email client (Gmail / Outlook): From / To / Subject /
 * Date in the header, status badge, then the sanitized bodyHtml inside
 * a sandboxed iframe so the email's own CSS can't bleed into the app.
 *
 * Sender display name + reply-to email come from the org's emailConfig
 * (sendgrid_reply_to_name / sendgrid_reply_to_email) — same fields the
 * Bulk eAlert setup uses. Falls back to the actual `fromAddress` on the
 * message when those org fields aren't set.
 */
export const EmailPreviewModal: React.FC<EmailPreviewModalProps> = ({
  isOpen,
  onClose,
  email,
  contactName,
  contactEmail,
}) => {
  const organization = useOrganizationStore((s) => s.organization);

  // Sandbox the email body in a srcdoc iframe so its own styles can't
  // affect the rest of the app. Emails are designed for a white canvas,
  // so we keep the iframe light regardless of the app's theme — forcing
  // dark inside the iframe inverted text colors but left email-built
  // containers (property cards, etc.) still white, producing light-on-
  // light unreadable rows.
  const srcDoc = useMemo(() => {
    if (!email?.bodyHtml && !email?.body) return '';
    const inner = email.bodyHtml
      ? sanitizeEmailHtml(email.bodyHtml)
      : `<pre style="font-family: ui-sans-serif, system-ui, sans-serif; white-space: pre-wrap; word-wrap: break-word; margin: 0;">${escapeHtml(
          email.body || ''
        )}</pre>`;
    return `<!doctype html>
<html><head><meta charset="utf-8"><base target="_blank">
<style>
  html, body { margin: 0; padding: 0; color-scheme: light; }
  body {
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
    color: #1f2937;
    background: #ffffff;
    padding: 16px 18px;
    line-height: 1.5;
    font-size: 14px;
  }
  pre { color: #1f2937; }
  a { color: inherit; text-decoration: underline; pointer-events: none; }
  img { max-width: 100%; height: auto; }
  table { border-collapse: collapse; max-width: 100%; }

  /* Match the app-wide .scrollbar-custom styling so the iframe doesn't
     surface a default-chrome browser scrollbar. */
  html { scrollbar-gutter: stable; }
  ::-webkit-scrollbar { width: 0.55rem; height: 0.55rem; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.25);
    border-radius: 9999px;
    border: 2px solid transparent;
    background-clip: content-box;
  }
  ::-webkit-scrollbar-thumb:hover {
    background: rgba(0, 0, 0, 0.45);
    background-clip: content-box;
  }
</style>
</head><body>${inner}</body></html>`;
  }, [email]);

  if (!email) return null;

  // Header fields.
  const senderName =
    organization?.sendgrid_reply_to_name ||
    organization?.assistant_name ||
    organization?.org_name ||
    'Remi';
  const senderEmail =
    organization?.sendgrid_sender_email ||
    organization?.sendgrid_reply_to_email ||
    organization?.reply_to_email ||
    email.fromAddress ||
    '';
  const sentAt = email.sentAt || email.deliveredAt || email.openedAt || email.createdAt;
  const timezone = organization?.timezone;
  // For outbound emails the lead is the recipient; for inbound the lead
  // is the sender. Decorate the right side with the contact's name.
  const isInbound = email.direction === 'inbound';

  // Subject fallback chain — `subject` is often empty on certain message
  // sources, so use the template name when present, then strip a short
  // snippet from the body so the modal never headlines with "(no subject)"
  // when there's clearly real content underneath.
  const subjectDisplay =
    email.subject?.trim() ||
    (() => {
      const raw = email.bodyHtml || email.body || '';
      if (!raw) return '';
      // Drop style/script before extracting text — otherwise the email's
      // own CSS leaks into the subject snippet (e.g. "body { margin: 0;
      // padding: 0; ..." from eAlert templates).
      let stripped: string;
      if (typeof window !== 'undefined' && email.bodyHtml) {
        const tmp = document.createElement('div');
        tmp.innerHTML = email.bodyHtml;
        tmp.querySelectorAll('style, script, noscript').forEach((n) => n.remove());
        stripped = (tmp.textContent || tmp.innerText || '').replace(/\s+/g, ' ').trim();
      } else {
        stripped = raw
          .replace(/<style[\s\S]*?<\/style>/gi, '')
          .replace(/<script[\s\S]*?<\/script>/gi, '')
          .replace(/<[^>]+>/g, '')
          .replace(/\s+/g, ' ')
          .trim();
      }
      return stripped.length > 80 ? stripped.slice(0, 80) + '…' : stripped;
    })() ||
    '(no subject)';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      className="h-[100dvh] md:h-[calc(100vh-1rem)] 3xl:h-[calc(100vh-3.5rem)]"
    >
      <Modal.Header>
        {/* Header mirrors the EmailsTab row layout (sans the envelope
            icon): subject as the headline, sender + recipient stacked
            below, status + time pinned to the right. */}
        <div className="flex flex-col gap-1.5 min-w-0 w-full">
          <div className="flex items-baseline justify-between gap-3 min-w-0">
            <div className="text-[15px] font-semibold text-foreground tracking-tight truncate">
              {subjectDisplay}
            </div>
            <span className="text-[10.5px] text-muted-foreground tabular-nums whitespace-nowrap flex-shrink-0">
              {sentAt && formatDate(sentAt, timezone, 'MMM D, YYYY h:mm A z')}
            </span>
          </div>
          {/* From + To kept tight together. Use real text-spaces between
              the label and the value — Tailwind's `mr-*` on inline spans
              is unreliable next to `truncate`, so " " + nbsp via JSX gives
              a guaranteed visible gap. */}
          <div className="flex flex-col gap-1.5 leading-tight">
            <div className="text-[12px] text-foreground/85 truncate leading-tight">
              <span className="text-muted-foreground">From</span>{' '}
              <span className="font-medium">
                {isInbound ? contactName || email.fromAddress || '—' : senderName}
              </span>
              {(() => {
                const fromEmail = isInbound ? contactEmail || email.fromAddress : senderEmail;
                return fromEmail ? (
                  <span className="text-muted-foreground"> &lt;{fromEmail}&gt;</span>
                ) : null;
              })()}
            </div>
            <div className="text-[12px] text-foreground/85 truncate leading-tight">
              <span className="text-muted-foreground">To</span>{' '}
              <span className="font-medium">
                {isInbound ? senderName : contactName || email.toAddress || '—'}
              </span>
              {(() => {
                const toEmail = isInbound ? senderEmail : contactEmail || email.toAddress;
                return toEmail ? (
                  <span className="text-muted-foreground"> &lt;{toEmail}&gt;</span>
                ) : null;
              })()}
            </div>
          </div>
        </div>
      </Modal.Header>
      <Modal.Body className="!overflow-hidden flex-1 min-h-0 flex flex-col">
        <div className="flex flex-col flex-1 min-h-0 rounded-md border border-foreground/10 overflow-hidden">
          {srcDoc ? (
            <iframe
              title="Email body"
              srcDoc={srcDoc}
              sandbox="allow-same-origin"
              className="w-full flex-1 min-h-0 border-0"
            />
          ) : (
            <div className="flex items-center justify-center flex-1 text-sm text-muted-foreground italic">
              No body content available for this email.
            </div>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}
